import { InputNumber } from "@/components/InputNumber";
import {
  datePickerClearableProps,
  datePickerFormItemProps,
  datePickerStandardProps,
  quillEditorStandardProps,
  quillFormItemProps,
  selectStandardProps,
} from "@/utils/componentUtils";
import { dateToIsoDateString, toDate } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { Card, Col, DatePicker, Form, Row, Select } from "antd";
import { Rule } from "antd/lib/form";
import { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill-new";
import { rowGutter } from "../../../../../../common/constants";
import { LoanRateType } from "../../../../enums";
import { CreateUpdateContractGainerRecord, CreateUpdateLoanContract } from "../../../../types";
import {
  calculateContactClientDate,
  calculateContractStatus,
  calculateGainerRecordStartDates,
} from "../../../../utils";
import ContractStatusTag from "../../../tags/ContractStatusTag";

const colSpan = 4;

const LoanContractFormDataSection = () => {
  const { t } = useTranslation();

  const form = Form.useFormInstance<CreateUpdateLoanContract>();

  const status = Form.useWatch("status", form);

  const applicationSignDate = Form.useWatch("applicationSignDate", form);
  const mediationReportSignDateRules: Rule[] = applicationSignDate
    ? [validations.notNull]
    : [validations.notNull, validations.notAfter(applicationSignDate, t("contract.attrs.applicationSignDate"))];

  const signDate = Form.useWatch("signDate", form);

  const applicationSignDateRules = signDate
    ? [validations.notNull]
    : [validations.notNull, validations.notAfter(signDate, t("contract.attrs.signDate"))];

  const maturityDateRules = signDate
    ? [validations.notNull]
    : [validations.notNull, validations.notSameOrBefore(signDate, t("contract.attrs.signDate"))];

  const cancellationDate = Form.useWatch("cancellationDate", form);

  const loanMaturityDate = Form.useWatch("loanMaturityDate", form);

  const gainerRecords = Form.useWatch("gainerRecords", form);

  const approvedAmount = Form.useWatch("approvedAmount", form);

  const handleStatusDefiningDateChange = (signDate: string, loanMaturityDate: string, cancellationDate?: string) => {
    form.setFieldsValue({
      status: calculateContractStatus({
        startDate: signDate,
        endDate: loanMaturityDate,
        cancellationDate: cancellationDate,
        transferredToOtherBrokerDate: undefined,
      }),
    });
  };

  const handleRateTypeChange = (rateType: LoanRateType) => {
    if (rateType === LoanRateType.VARIABLE) {
      form.setFieldsValue({ fixationAnniversaryDate: undefined, contactClientDate: undefined });
    }
  };

  const handleFixationAnniversaryDateChange = (fixationAnniversaryDate: Dayjs | null) => {
    if (fixationAnniversaryDate) {
      form.setFieldsValue({ contactClientDate: calculateContactClientDate(fixationAnniversaryDate) });
    }
  };

  const handleSignDateChange = (
    signDate: string,
    loanMaturityDate: string,
    cancellationDate: string | undefined,
    gainerRecords: CreateUpdateContractGainerRecord[],
  ) => {
    handleStatusDefiningDateChange(signDate, loanMaturityDate, cancellationDate);

    form.setFieldsValue({ gainerRecords: calculateGainerRecordStartDates(gainerRecords, signDate) });
  };

  return (
    <Card
      type="inner"
      className="card-box card-box--inner-extra"
      title={t("contract.sections.contractData")}
      extra={status ? <ContractStatusTag status={status} /> : undefined}
    >
      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="mediationReportSignDate"
            label={t("contract.attrs.mediationReportSignDate")}
            rules={mediationReportSignDateRules}
            {...datePickerFormItemProps}
          >
            <DatePicker {...datePickerStandardProps} maxDate={toDate(applicationSignDate)} />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="applicationSignDate"
            label={t("contract.attrs.applicationSignDate")}
            rules={applicationSignDateRules}
            {...datePickerFormItemProps}
          >
            <DatePicker {...datePickerStandardProps} maxDate={toDate(signDate)} />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="signDate"
            label={t("contract.attrs.signDate")}
            rules={[validations.notNull]}
            {...datePickerFormItemProps}
          >
            <DatePicker
              {...datePickerStandardProps}
              onChange={(value) =>
                value
                  ? handleSignDateChange(dateToIsoDateString(value), loanMaturityDate, cancellationDate, gainerRecords)
                  : undefined
              }
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="loanMaturityDate"
            label={t("contract.attrs.loanMaturityDate")}
            rules={maturityDateRules}
            {...datePickerFormItemProps}
          >
            <DatePicker
              {...datePickerStandardProps}
              minDate={toDate(signDate)?.add(1, "day")}
              onChange={(value) =>
                value
                  ? handleStatusDefiningDateChange(signDate, dateToIsoDateString(value), cancellationDate)
                  : undefined
              }
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="cancellationDate"
            label={t("contract.attrs.cancellationDate")}
            rules={[
              signDate ? validations.notBefore(signDate, t("contract.attrs.signDate")) : validations.none,
              loanMaturityDate
                ? validations.notSameOrAfter(loanMaturityDate, t("contract.attrs.loanMaturityDate"))
                : validations.none,
            ]}
            {...datePickerFormItemProps}
          >
            <DatePicker
              {...datePickerClearableProps}
              minDate={toDate(signDate)}
              maxDate={toDate(loanMaturityDate)?.subtract(1, "day")}
              onChange={(value) =>
                handleStatusDefiningDateChange(
                  signDate,
                  loanMaturityDate,
                  value ? dateToIsoDateString(value) : undefined,
                )
              }
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="ltvRatio"
            label={t("contract.attrs.ltvRatio")}
            rules={[validations.minNumber(0.01), validations.maxNumber(100)]}
          >
            <InputNumber type="decimal" addon="percentage" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="approvedAmount"
            label={t("contract.attrs.approvedAmount")}
            rules={[validations.notNull, validations.minNumber(0.01)]}
          >
            <InputNumber type="decimal" addon="euro" />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="monthlyPaymentAmount"
            label={t("contract.attrs.monthlyPaymentAmount")}
            rules={[
              validations.notNull,
              validations.minNumber(0.01),
              validations.maxNumber(approvedAmount, t("contract.attrs.approvedAmount")),
            ]}
          >
            <InputNumber type="decimal" addon="euro" />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="interestRate"
            label={t("contract.attrs.interestRate")}
            rules={[validations.notNull, validations.minNumber(0.01), validations.maxNumber(100)]}
          >
            <InputNumber type="decimal" addon="percentage" />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item name="rateType" label={t("contract.enums.loanRateType._label")} rules={[validations.notNull]}>
            <Select
              {...selectStandardProps}
              options={Object.keys(LoanRateType).map((type) => ({
                value: type,
                label: t("contract.enums.loanRateType." + type),
              }))}
              onChange={handleRateTypeChange}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="fixationAnniversaryDate"
            label={t("contract.attrs.fixationAnniversaryDate")}
            rules={[
              validations.notNull,
              signDate ? validations.notSameOrBefore(signDate, t("contract.attrs.signDate")) : validations.none,
              loanMaturityDate
                ? validations.notAfter(loanMaturityDate, t("contract.attrs.loanMaturityDate"))
                : validations.none,
            ]}
            {...datePickerFormItemProps}
          >
            <DatePicker
              {...datePickerStandardProps}
              minDate={toDate(signDate)?.add(1, "day")}
              maxDate={toDate(loanMaturityDate)}
              onChange={handleFixationAnniversaryDateChange}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="contactClientDate"
            label={t("contract.attrs.contactClientDate")}
            rules={[validations.notNull]}
            {...datePickerFormItemProps}
          >
            <DatePicker {...datePickerStandardProps} disabled />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={24}>
          <Form.Item
            name="note"
            label={t("contract.attrs.note")}
            rules={[validations.size(1, 8192)]}
            {...quillFormItemProps}
          >
            <ReactQuill {...quillEditorStandardProps} />
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );
};

export default LoanContractFormDataSection;
